import type { AskTurnStage } from '@/services/ask/api';

type StageCopy = Record<AskTurnStage, readonly string[]>;

export const ASK_PROGRESS_STAGES: readonly AskTurnStage[] = ['planning', 'searching', 'grading'];

/**
 * Planning copy for a first message that reads as a question. Kept apart from
 * the neutral pool because many first messages are search terms, not
 * questions, and "your question" misdescribes those.
 */
const QUESTION_PLANNING = ['Understanding your question', 'Reading your question'];

/**
 * Each step is hashed on its own, so any planning label can sit above any
 * searching label. Keep every word distinct across a set's steps, or two steps
 * of one turn can read as the same thing twice.
 */
const FIRST_TURN_COPY: StageCopy = {
  planning: ['Reading your request', 'Working out what to find'],
  searching: [
    'Searching answered inquiries',
    'Checking expert answers',
    'Finding similar inquiries',
  ],
  grading: ['Ranking matches', 'Picking the best fits', 'Sorting by relevance'],
};

const FOLLOW_UP_COPY: StageCopy = {
  planning: ['Reading your follow-up', 'Adding the new detail', 'Taking in what you added'],
  searching: ['Searching again', 'Checking more inquiries', 'Looking for more answers'],
  grading: ['Re-ranking results', 'Weighing each result', 'Narrowing the matches'],
};

/** A stable, non-cryptographic hash, so a turn keeps its wording across renders. */
function hashSeed(seed: string): number {
  let hash = 0;

  for (let index = 0; index < seed.length; index++) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }

  return hash;
}

/**
 * The label for each progress step of one turn. Wording varies between turns
 * but is chosen from the turn id, never at random on render, so a turn does not
 * reshuffle its steps while it runs or when the thread remounts.
 *
 * Follow-ups get their own wording, since the search builds on what came before.
 * The server can later decide a follow-up was a new case, but it only says so
 * once the turn finishes, so every turn after the first is worded as a
 * follow-up while it runs.
 */
export function askProgressLabels({
  seed,
  text,
  isFollowUp,
}: {
  seed: string;
  text: string;
  isFollowUp: boolean;
}): Record<AskTurnStage, string> {
  const copy = isFollowUp ? FOLLOW_UP_COPY : FIRST_TURN_COPY;

  const planningPool = !isFollowUp && text.includes('?') ? QUESTION_PLANNING : copy.planning;

  const pick = (pool: readonly string[], stage: AskTurnStage) =>
    pool[hashSeed(`${seed}:${stage}`) % pool.length] ?? pool[0];

  return {
    planning: pick(planningPool, 'planning'),
    searching: pick(copy.searching, 'searching'),
    grading: pick(copy.grading, 'grading'),
  };
}
